/**
 * discountVoid.ts — Pure computation of the Discount & Void audit: the margin
 * given away at the till, and the sales that were cancelled — both attributed to
 * the staff member who raised the invoice, which nothing in the app surfaces
 * today.
 *
 * Framing: all figures are for SALE invoices *raised* in the range (by
 * `createdAt`). Discounts are counted only on live sales — a voided invoice's
 * discount never happened. Voids are counted on invoices now in a VOIDED state.
 *
 *  - **Discount %** is measured against the pre-discount taxable value
 *    (`taxableAmount + discountAmount`), so it reads as "how much of the ticket
 *    was given away", independent of tax.
 *  - **Void rate** is voided invoices as a share of all sales raised — the
 *    headline shrinkage/oversight signal.
 *
 * Pure — see `discountVoidDataSource.ts` for the WatermelonDB bridge.
 */

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const safeDivide = (num: number, den: number): number => (den > 0 ? num / den : 0);

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface AuditInvoiceInput {
  invoiceNumber: string;
  createdAt: Date;
  createdById: string;
  isVoided: boolean;
  discountAmount: number;
  /** Post-discount, pre-tax taxable value. */
  taxableAmount: number;
  totalAmount: number;
  /** Reason parsed from the void note, if any. */
  voidReason: string | null;
}

export interface DiscountVoidInput {
  /** SALE invoices raised in the range (voided and live). */
  invoices: AuditInvoiceInput[];
  /** userId → display name. */
  staffNames: Map<string, string>;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface StaffDiscount {
  staffId: string;
  name: string;
  invoiceCount: number;
  discountTotal: number;
  /** Pre-discount taxable base these discounts were given against. */
  preDiscountBase: number;
  discountPct: number;
}

export interface VoidItem {
  invoiceNumber: string;
  date: Date;
  staffId: string;
  staffName: string;
  value: number;
  reason: string | null;
}

export interface StaffVoid {
  staffId: string;
  name: string;
  count: number;
  value: number;
}

export interface DiscountVoidReport {
  // ── Discounts (live sales only) ──
  discountTotal: number;
  discountedCount: number;
  totalPreDiscountBase: number;
  overallDiscountPct: number;
  discountByStaff: StaffDiscount[];

  // ── Voids ──
  voidCount: number;
  voidValue: number;
  voids: VoidItem[];
  voidByStaff: StaffVoid[];

  // ── Context ──
  saleCount: number;
  saleValue: number;
  /** Voided invoices ÷ all sales raised (by count). */
  voidRatePct: number;
}

export function buildDiscountVoid(input: DiscountVoidInput): DiscountVoidReport {
  const { invoices, staffNames } = input;
  const nameOf = (id: string) => staffNames.get(id)?.trim() || 'Unknown';

  const live = invoices.filter((i) => !i.isVoided);
  const voided = invoices.filter((i) => i.isVoided);

  // ── Discounts, per staff ──
  interface DiscAcc { count: number; discount: number; base: number }
  const discAcc = new Map<string, DiscAcc>();
  let discountTotal = 0;
  let totalPreDiscountBase = 0;
  for (const inv of live) {
    if (inv.discountAmount <= 0) continue;
    const base = inv.taxableAmount + inv.discountAmount;
    discountTotal += inv.discountAmount;
    totalPreDiscountBase += base;
    const a = discAcc.get(inv.createdById) ?? { count: 0, discount: 0, base: 0 };
    a.count += 1;
    a.discount += inv.discountAmount;
    a.base += base;
    discAcc.set(inv.createdById, a);
  }
  const discountByStaff: StaffDiscount[] = [...discAcc.entries()]
    .map(([staffId, a]) => ({
      staffId,
      name: nameOf(staffId),
      invoiceCount: a.count,
      discountTotal: round2(a.discount),
      preDiscountBase: round2(a.base),
      discountPct: safeDivide(a.discount, a.base),
    }))
    .sort((x, y) => y.discountTotal - x.discountTotal);

  // ── Voids ──
  const voids: VoidItem[] = voided
    .map((inv) => ({
      invoiceNumber: inv.invoiceNumber,
      date: inv.createdAt,
      staffId: inv.createdById,
      staffName: nameOf(inv.createdById),
      value: round2(inv.totalAmount),
      reason: inv.voidReason,
    }))
    .sort((a, b) => b.value - a.value);

  const voidAcc = new Map<string, { count: number; value: number }>();
  for (const v of voids) {
    const a = voidAcc.get(v.staffId) ?? { count: 0, value: 0 };
    a.count += 1;
    a.value += v.value;
    voidAcc.set(v.staffId, a);
  }
  const voidByStaff: StaffVoid[] = [...voidAcc.entries()]
    .map(([staffId, a]) => ({ staffId, name: nameOf(staffId), count: a.count, value: round2(a.value) }))
    .sort((x, y) => y.value - x.value);

  const saleValue = live.reduce((s, i) => s + i.totalAmount, 0);
  const voidValue = voids.reduce((s, v) => s + v.value, 0);

  return {
    discountTotal: round2(discountTotal),
    discountedCount: live.filter((i) => i.discountAmount > 0).length,
    totalPreDiscountBase: round2(totalPreDiscountBase),
    overallDiscountPct: safeDivide(discountTotal, totalPreDiscountBase),
    discountByStaff,
    voidCount: voids.length,
    voidValue: round2(voidValue),
    voids,
    voidByStaff,
    saleCount: live.length,
    saleValue: round2(saleValue),
    voidRatePct: safeDivide(voids.length, live.length + voids.length),
  };
}

/** Extract the reason from an invoice note carrying a `VOID: …` line. */
export function parseVoidReason(notes: string | null): string | null {
  if (!notes) return null;
  const match = notes.match(/VOID:\s*(.+)/);
  if (!match) return null;
  const reason = match[1].trim();
  if (!reason || /^no reason( provided)?$/i.test(reason)) return null;
  return reason;
}
